import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import Header from "@/components/layout/header";
import { 
  Globe, Upload, Download, History, Search, Check, X, 
  Edit, Grid3X3, List, ChevronDown, Languages, CheckSquare,
  Calendar, FileText, MoreVertical, Filter, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import type { Translation, TranslationHistory } from "@/types/shared";

interface LocalizationStats {
  totalKeys: number;
  translatedKeys: number;
  missingKeys: number;
  lastImportDate?: string;
}

interface Language {
  language: string;
  count: number;
}

export default function LocalizationDashboard() {
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [featureFilter, setFeatureFilter] = useState("all");
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedTranslationId, setSelectedTranslationId] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [addLanguageModalOpen, setAddLanguageModalOpen] = useState(false);
  const [newLanguageCode, setNewLanguageCode] = useState("");
  const { toast } = useToast();

  // Queries
  const { data: languages = [], isLoading: languagesLoading } = useQuery<Language[]>({
    queryKey: ['/api/translations/languages'],
  });

  const { data: stats, isLoading: statsLoading } = useQuery<LocalizationStats>({
    queryKey: ['/api/translations/stats', selectedLanguage],
    enabled: !!selectedLanguage,
  });

  const { 
    data: translations = [], 
    isLoading: translationsLoading 
  } = useQuery<Translation[]>({
    queryKey: ['/api/translations', {
      language: selectedLanguage,
      feature: featureFilter !== 'all' ? featureFilter : undefined,
      search: searchTerm || undefined,
    }],
  });

  const { data: translationHistory = [] } = useQuery<TranslationHistory[]>({
    queryKey: ['/api/translations', selectedTranslationId, 'history'],
    enabled: !!selectedTranslationId && historyModalOpen,
  });

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      return apiRequest('PUT', `/api/translations/${id}`, { value });
    },
    onSuccess: () => {
      toast({
        title: "Translation updated",
        description: "The translation has been saved successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/translations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/translations/stats'] });
      setEditingId(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update translation.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(
        ids.map(id => apiRequest('DELETE', `/api/translations/${id}`))
      );
    },
    onSuccess: () => {
      toast({
        title: "Translations deleted",
        description: `${selectedKeys.length} translation(s) deleted successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/translations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/translations/stats'] });
      setSelectedKeys([]);
    },
  });

  // Filter translations
  const filteredTranslations = translations.filter(t => {
    if (statusFilter === 'translated' && (!t.value || t.value.trim() === '')) return false;
    if (statusFilter === 'missing' && t.value && t.value.trim() !== '') return false;
    return true;
  });

  // Handlers
  const handleEdit = (translation: Translation) => {
    setEditingId(translation.id);
    setEditingValue(translation.value || '');
  };

  const handleSave = () => {
    if (editingId !== null) {
      updateMutation.mutate({ id: editingId, value: editingValue });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const handleSelectAll = () => {
    if (selectedKeys.length === filteredTranslations.length) {
      setSelectedKeys([]);
    } else {
      setSelectedKeys(filteredTranslations.map(t => t.id));
    }
  };

  const handleSelectKey = (id: string) => {
    setSelectedKeys(prev =>
      prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]
    );
  };

  const handleShowHistory = (translationId: string) => {
    setSelectedTranslationId(translationId);
    setHistoryModalOpen(true);
  };

  const handleExport = () => {
    const csvContent = [
      ['Key', 'Default (English)', selectedLanguage.toUpperCase(), 'Status', 'Feature'],
      ...filteredTranslations.map(t => [
        t.key,
        translations.find(tr => tr.key === t.key && tr.language === 'en')?.value || '',
        t.value || '',
        t.value ? 'Translated' : 'Missing',
        t.feature || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `translations-${selectedLanguage}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (languagesLoading || statsLoading) {
    return (
      <>
        <Header 
          title="Localization Dashboard" 
          description="Manage translation strings and language support for all tenants"
        />
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map(i => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header 
        title="Localization Dashboard" 
        description="Manage translation strings and language support for all tenants"
      />
      
      <div className="flex-1 overflow-auto p-6">
        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-48">
                <Languages className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select Language" />
              </SelectTrigger>
              <SelectContent>
                {languages.map(lang => (
                  <SelectItem key={lang.language} value={lang.language}>
                    {lang.language.toUpperCase()} ({lang.count} keys)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Button onClick={() => setAddLanguageModalOpen(true)} className="athlyn-button-primary">
              <Globe className="w-4 h-4 mr-2" />
              Add New Language
            </Button>
            <Button onClick={() => setImportModalOpen(true)} variant="outline">
              <Upload className="w-4 h-4 mr-2" />
              Import Translations
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Keys</p>
                  <p className="text-2xl font-semibold">{stats?.totalKeys || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Translated</p>
                  <p className="text-2xl font-semibold text-green-600">{stats?.translatedKeys || 0}</p>
                </div>
                <Check className="w-8 h-8 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Missing</p>
                  <p className="text-2xl font-semibold text-orange-600">{stats?.missingKeys || 0}</p>
                </div>
                <X className="w-8 h-8 text-orange-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Last Import</p>
                  <p className="text-sm font-medium">
                    {stats?.lastImportDate 
                      ? new Date(stats.lastImportDate).toLocaleDateString()
                      : 'Never'}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="athlyn-card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search keys..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 athlyn-input"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="translated">Translated</SelectItem>
                    <SelectItem value="missing">Missing</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={featureFilter} onValueChange={setFeatureFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Feature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Features</SelectItem>
                    <SelectItem value="dashboard">Dashboard</SelectItem>
                    <SelectItem value="activities">Activities</SelectItem>
                    <SelectItem value="settings">Settings</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setFeatureFilter("all");
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
                
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'card' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('card')}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedKeys.length > 0 && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4 flex items-center justify-between">
            <span className="text-sm font-medium">
              {selectedKeys.length} key(s) selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleExport}
              >
                <Download className="w-4 h-4 mr-2" />
                Export Selected
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => deleteMutation.mutate(selectedKeys)}
              >
                Delete Selected
              </Button>
            </div>
          </div>
        )}

        {/* Translations Table/Cards */}
        {translationsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : filteredTranslations.length === 0 ? (
          <Card className="athlyn-card">
            <CardContent className="p-12 text-center">
              <Languages className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No translations found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try adjusting your search filters' : 'Add your first translation key'}
              </p>
              <Button className="athlyn-button-primary">
                Add New Key
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
          <Card className="athlyn-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="athlyn-table-header">
                      <Checkbox
                        checked={selectedKeys.length === filteredTranslations.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="athlyn-table-header text-left">Key Identifier</th>
                    <th className="athlyn-table-header text-left">Default Text</th>
                    <th className="athlyn-table-header text-left">Translated Text</th>
                    <th className="athlyn-table-header text-left">Status</th>
                    <th className="athlyn-table-header text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTranslations.map((translation) => {
                    const defaultText = translations.find(
                      t => t.key === translation.key && t.language === 'en'
                    )?.value || '';
                    
                    return (
                      <tr key={translation.id} className="border-b hover:bg-muted/50">
                        <td className="athlyn-table-cell">
                          <Checkbox
                            checked={selectedKeys.includes(translation.id)}
                            onCheckedChange={() => handleSelectKey(translation.id)}
                          />
                        </td>
                        <td className="athlyn-table-cell font-mono text-sm">
                          {translation.key}
                        </td>
                        <td className="athlyn-table-cell text-sm text-muted-foreground">
                          {defaultText}
                        </td>
                        <td className="athlyn-table-cell">
                          {editingId === translation.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                className="h-8 text-sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSave();
                                  if (e.key === 'Escape') handleCancel();
                                }}
                              />
                              <Button size="sm" onClick={handleSave}>
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={handleCancel}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div 
                              className="cursor-pointer hover:bg-muted px-2 py-1 rounded"
                              onClick={() => handleEdit(translation)}
                            >
                              {translation.value || <span className="text-muted-foreground italic">Click to add</span>}
                            </div>
                          )}
                        </td>
                        <td className="athlyn-table-cell">
                          <Badge variant={translation.value ? "default" : "secondary"}>
                            {translation.value ? 'Translated' : 'Missing'}
                          </Badge>
                        </td>
                        <td className="athlyn-table-cell">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(translation)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleShowHistory(translation.id)}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTranslations.map((translation) => {
              const defaultText = translations.find(
                t => t.key === translation.key && t.language === 'en'
              )?.value || '';
              
              return (
                <Card key={translation.id} className="athlyn-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedKeys.includes(translation.id)}
                          onCheckedChange={() => handleSelectKey(translation.id)}
                        />
                        <div>
                          <code className="text-sm font-mono">{translation.key}</code>
                          <Badge 
                            variant={translation.value ? "default" : "secondary"} 
                            className="ml-2"
                          >
                            {translation.value ? 'Translated' : 'Missing'}
                          </Badge>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(translation)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShowHistory(translation.id)}>
                            <History className="w-4 h-4 mr-2" />
                            View History
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Default Text</Label>
                        <p className="text-sm mt-1">{defaultText}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Translated Text</Label>
                        {editingId === translation.id ? (
                          <div className="mt-1">
                            <Textarea
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="text-sm"
                              rows={3}
                              autoFocus
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <Button size="sm" onClick={handleSave}>
                                Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={handleCancel}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="mt-1 cursor-pointer hover:bg-muted p-2 rounded"
                            onClick={() => handleEdit(translation)}
                          >
                            {translation.value || <span className="text-muted-foreground italic">Click to add translation</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Translation History</DialogTitle>
            <DialogDescription>
              View all changes made to this translation key
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-96 pr-4">
            <div className="space-y-4">
              {translationHistory.map((history) => (
                <div key={history.id} className="border-l-2 border-primary/20 pl-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">
                      {new Date(history.createdAt).toLocaleString()}
                    </p>
                    <Button size="sm" variant="ghost">
                      Revert
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded text-sm">
                      <span className="text-red-600 dark:text-red-400">- </span>
                      {history.previousValue || '(empty)'}
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded text-sm">
                      <span className="text-green-600 dark:text-green-400">+ </span>
                      {history.newValue}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Translations</DialogTitle>
            <DialogDescription>
              Upload a CSV or JSON file to import translations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                Drop your file here or click to browse
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Supports CSV and JSON formats
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportModalOpen(false)}>
              Cancel
            </Button>
            <Button className="athlyn-button-primary">
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Language Modal */}
      <Dialog open={addLanguageModalOpen} onOpenChange={setAddLanguageModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Language</DialogTitle>
            <DialogDescription>
              Add support for a new language in the system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Language Code</Label>
              <Input
                placeholder="e.g. fr, es, de"
                value={newLanguageCode}
                onChange={(e) => setNewLanguageCode(e.target.value)}
                className="mt-2"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Use ISO 639-1 language codes
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLanguageModalOpen(false)}>
              Cancel
            </Button>
            <Button className="athlyn-button-primary">
              Add Language
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}